'use client';
import { Box, Container, Heading, SimpleGrid, Stack } from '@chakra-ui/react';
import { getImageProps } from 'next/image';
import React from 'react';

import { getImageLink } from '@/services/link.service';
import { directusImageLoader } from '@/utils/loaders';

import Styles from './Article.module.scss';

type SectionProps = {
  description: string;
  image_id: string;
  title: string;
};

type MosaicsProps = {
  mosaics: SectionProps[];
};

const ContentSection: React.FC<SectionProps> = ({ title, description }) => (
  <Stack
    className={Styles.serviceBlockDetails}
    order={{ base: '2', lg: '1' }}
    paddingY={{ base: '14', md: '14', lg: '70px' }}
    paddingX={{ base: '6', md: '6', lg: '70px' }}
    alignContent="center"
  >
    <Box className={Styles.headingElement}>
      <Heading
        as="h4"
        className={Styles.titleMd}
        marginBottom={{ base: 4, md: 5 }}
      >
        {title}
      </Heading>
      <div
        dangerouslySetInnerHTML={{
          __html: description,
        }}
        className={Styles.titleText}
      ></div>
    </Box>
  </Stack>
);

function Mosaics({ article }: { article: MosaicsProps }) {
  return (
    <Container
      maxW={1328}
      width={'100%'}
      marginX={'auto'}
      paddingX={{ base: 0 }}
    >
      <Box paddingY={{ md: 14 }}>
        {article?.mosaics?.map((section, i) => {
          if (!section?.image_id) {
            return <ContentSection key={i} {...section} />;
          }

          const common = {
            alt: 'Hero',
            width: 664,
            height: 406,
            loader: directusImageLoader,
          };
          const imageKeys = {
            mobileKey: 'image-360-228',
            desktopKey: 'image-664-406',
          };
          const {
            props: { srcSet: mobile },
          } = getImageProps({
            ...common,
            src: `${getImageLink(section.image_id) ?? ''}?key=${imageKeys.mobileKey}`,
          });
          const {
            props: { srcSet: desktop, ...rest },
          } = getImageProps({
            ...common,
            src: `${getImageLink(section.image_id) ?? ''}?key=${imageKeys.desktopKey}`,
          });

          return (
            <SimpleGrid
              className={Styles.serviceBlockContent}
              key={i}
              columns={[1, 1, 1, 2]}
              alignItems={['center']}
            >
              <Box
                className={Styles.serviceImgBlock}
                order={i % 2 ? {} : { base: '2', lg: '2' }}
              >
                <picture className={Styles.serviceImage}>
                  <source media="(max-width: 575px)" srcSet={mobile} />
                  <source media="(min-width: 576px)" srcSet={desktop} />
                  <img {...rest} alt="hero-banner-image" />
                </picture>
              </Box>
              <ContentSection {...section} />
            </SimpleGrid>
          );
        })}
      </Box>
    </Container>
  );
}

export default Mosaics;
